import type { CreateSetup } from '@here/api'
import { Field } from '@here/components'
import { type FormStore, getValue, setValue } from '@modular-forms/solid'
import { Crosshair } from 'lucide-solid'
import { For, Show, createEffect, createSignal } from 'solid-js'
import { Flex, Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import type { FormFieldType } from '.'
import { FieldGroupContainer } from './FieldGroupContainer'
import { ComputedSchema } from './createSetupSchema'

export default function Computed(props: {
	FormField: FormFieldType
	form: FormStore<CreateSetup, undefined>
}) {
	const [isValid, setIsValid] = createSignal(false)

	createEffect(() => {
		const target = getValue(props.form, 'target')
		const axis = getValue(props.form, 'axis')
		const interim1 = getValue(props.form, 'interim_1')
		const interim2 = getValue(props.form, 'interim_2')
		const position = getValue(props.form, 'position')
		const riskRewardRatio = getValue(props.form, 'risk_reward_ratio')
		const timeStop = getValue(props.form, 'time_stop')
		const patternSpecification = getValue(props.form, 'pattern_specification')

		const validateResult = v.safeParse(ComputedSchema, {
			axis: axis,
			target: target,
			interim_1: interim1,
			interim_2: interim2,
			position: position,
			risk_reward_ratio: riskRewardRatio,
			time_stop: timeStop,
			pattern_specification: patternSpecification,
		})

		setIsValid(validateResult.success)
	})

	const formFields: Array<keyof CreateSetup> = ['interim_1', 'interim_2', 'target']

	return (
		<FieldGroupContainer
			heading="Computed"
			valid={isValid()}
			actions={
				<>
					{/* <Tooltip.Root
          openDelay={500}
          positioning={{
            placement: 'left',
          }}>
          <Tooltip.Trigger type='button'>
            <IconButton colorPalette={canEditComputed() ? 'mint' : 'gray'}
              size='xs'
              type='button'
              onClick={() => setCanEditComputed(!canEditComputed())}
              variant="subtle">
              <Show when={canEditComputed()} fallback={<PencilOff />}>
                <Pencil />
              </Show>
            </IconButton>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Arrow>
              <Tooltip.ArrowTip />
            </Tooltip.Arrow>
            <Tooltip.Content>
              <Text>{canEditComputed()
                ? 'Section is editable'
                : 'Section editing is disabled'
              }</Text>
            </Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root> */}
				</>
			}
		>
			<Grid
				gridTemplateColumns={{
					base: '1fr 1fr 1fr',
					lgDown: '1fr 1fr',
					mdDown: '1fr',
				}}
				pointerEvents="none"
			>
				<props.FormField name="pattern_specification">
					{(field, formFieldProps) => (
						<Field.Root invalid={!!field.error}>
							<Field.Label>Pattern Specification</Field.Label>
							<Field.Input
								{...formFieldProps}
								fontSize="14px"
								type="text"
								value={field.value as any}
							/>
							{/* <SelectField formFieldProps={formFieldProps}
                field={field}
                collection={createListCollection({
                  items: patternSpecificationList.map(value => ({ label: value.replaceAll('-', ' '), value }))
                })}
                onChange={(e) => setValue(props.form, field.name, e.value[0] as any)} /> */}
							<Field.ErrorText>
								{field.error && <div>{field.error}</div>}
							</Field.ErrorText>
						</Field.Root>
					)}
				</props.FormField>

				<props.FormField
					name="risk_reward_ratio"
					type="number"
				>
					{(field, formFieldProps) => (
						<Field.Root invalid={!!field.error}>
							<Field.Label>Risk Reward Ratio</Field.Label>
							<Field.Input
								{...formFieldProps}
								type="number"
								onChange={(event) =>
									setValue(props.form, 'risk_reward_ratio', Number(event.target.value))
								}
								value={field.value as any}
							/>
							<Field.ErrorText>
								{field.error && <div>{field.error}</div>}
							</Field.ErrorText>
						</Field.Root>
					)}
				</props.FormField>

				<props.FormField
					name="axis"
					type="number"
				>
					{(field, formFieldProps) => (
						<Field.Root invalid={!!field.error}>
							<Field.Label>Axis</Field.Label>
							<Field.Input
								{...formFieldProps}
								type="number"
								onChange={(event) =>
									setValue(props.form, 'axis', Number(event.target.value))
								}
								value={field.value as any}
							/>
							<Field.ErrorText>
								{field.error && <div>{field.error}</div>}
							</Field.ErrorText>
						</Field.Root>
					)}
				</props.FormField>

				<For each={formFields}>
					{(formField) => (
						<props.FormField
							name={formField}
							type="number"
						>
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<Flex
										alignItems="center"
										justifyContent="space-between"
									>
										<Field.Label>{formField.replaceAll('_', ' ')}</Field.Label>
										<Show when={formField === 'target'}>
											<Crosshair size="17px" />
										</Show>
									</Flex>
									<Field.Input
										{...formFieldProps}
										type="number"
										onChange={(event) =>
											setValue(props.form, formField, Number(event.target.value))
										}
										value={field.value as any}
									/>
									<Field.ErrorText>
										{field.error && <div>{field.error}</div>}
									</Field.ErrorText>
								</Field.Root>
							)}
						</props.FormField>
					)}
				</For>
			</Grid>
		</FieldGroupContainer>
	)
}
